import React from "react";
import { TableList } from "../TableList";
import { FormattedMessage } from "react-intl";
import { useTableOptions } from "../../../util/tableOptionsMaterialTable";
import DialogColumnSelector from "./DialogColumnSelector";
import { Box, Button, Grid, makeStyles, Typography } from "@material-ui/core";
import ErrorMessage from "../ErrorMessage";
import LoadingSpinner from "../LoadingSpinner";
import CopyCsvButton from "../CopyCsvButton";
import { SORT } from "../../../util";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import useSelectedColumns from "../../../hooks/useSelectedReportColumns";
import { useDispatch } from "react-redux";
import { showErrorMessage } from "../../../actions";

const useStyles = makeStyles((theme) => ({
  containerTitle: {
    marginBottom: theme.spacing(2),
  },
  title: {
    overflow: "visible",
    textOverflow: "unset",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  orderBy: {
    display: "flex",
    verticalAlign: "middle",
    alignItems: "center",
  },
  orderByLabel: {
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
  },
  icon: {
    transition: "transform 200ms ease",
    cursor: "pointer",
  },
  iconUp: {
    transform: "rotate(180deg)",
  },
}));

const ReportTable = ({
  availableColumns,
  currentUser,
  title,
  data = [],
  loading,
  loadingMessage = <FormattedMessage id="app.loading.fetching.reports" />,
  emptyMessage = "app.data.empty",
  error,
  enableCopy,
  fetchAllData,
  params,
  rowsPath,
  ExtraActions,
  orderBy,
  setOrderBy,
  sort,
  setSort,
  reportKey,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [openDialog, setOpenDialog] = React.useState(false);

  const [selectedColumns, setSelectedColumns] = useSelectedColumns(availableColumns, reportKey);

  const columns = React.useMemo(() => {
    return availableColumns
      .filter((col) => selectedColumns?.[col.name])
      .map((col) => {
        const definition = col.definition();
        const enableSort = col.enableSort ?? true;
        return {
          ...definition,
          exportValue: col.exportValue,
          enableSort,
          ...(!enableSort && {
            sorting: false,
            titleRaw: definition.title,
            title: (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(showErrorMessage("app.column.canNotSort"));
                }}
              >
                <FormattedMessage id={definition.title} />
              </span>
            ),
          }),
        };
      });
  }, [availableColumns, dispatch, selectedColumns]);

  const tableRenderKey = React.useMemo(() => columns.map((col) => col.field).join("|"), [columns]);

  const { hasData, hasColumns, isEmpty, disabled } = React.useMemo(() => {
    const hasData = data?.length > 0;
    const hasColumns = columns?.length > 0;

    return {
      hasData,
      hasColumns,
      isEmpty: !loading && !error && !hasData,
      disabled: loading || !hasColumns || !hasData,
    };
  }, [data, columns, loading, error]);

  const options = useTableOptions({
    toolbar: false,
  });

  const currentOrderLabel = React.useMemo(() => {
    const col = columns.find((c) => c.field === orderBy);
    return col?.title || orderBy;
  }, [columns, orderBy]);

  const handleOrderChange =
    setOrderBy && setSort
      ? (orderIndex) => {
          const column = columns[orderIndex];
          if (!column) return;

          const { field } = column;
          const isSameColumn = orderBy === field;

          const nextSort = isSameColumn ? (sort === SORT.ASC ? SORT.DESC : SORT.ASC) : SORT.DESC;

          setOrderBy(field);
          setSort(nextSort);
        }
      : undefined;

  return (
    <>
      <Grid
        container
        spacing={1}
        alignItems="center"
        className={classes.containerTitle}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
        >
          {title && (
            <Typography
              variant="h6"
              className={classes.title}
            >
              <FormattedMessage id={title} />
            </Typography>
          )}
          {currentOrderLabel && !disabled && (
            <Box className={classes.orderBy}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
              >
                <FormattedMessage id="app.orderBy" />
                <Box
                  component="span"
                  className={classes.orderByLabel}
                >
                  {currentOrderLabel?.startsWith("app.") ? <FormattedMessage id={currentOrderLabel} /> : currentOrderLabel}
                  <ArrowDownwardIcon
                    fontSize="small"
                    className={`${classes.icon} ${sort === SORT.DESC ? classes.iconUp : ""}`}
                    onClick={() => setSort(sort === SORT.ASC ? SORT.DESC : SORT.ASC)}
                  />
                </Box>
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
        >
          <Box className={classes.actions}>
            {ExtraActions && <ExtraActions disabled={disabled} />}
            {enableCopy && (
              <CopyCsvButton
                data={data}
                columns={columns}
                fetchAllData={fetchAllData}
                params={params}
                rowsPath={rowsPath}
                disabled={disabled}
              />
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setOpenDialog(true)}
              disabled={loading || error}
            >
              <FormattedMessage id="app.column.filter" />
            </Button>
          </Box>
        </Grid>
      </Grid>

      {hasColumns ? (
        <>
          {error && (
            <ErrorMessage>
              <FormattedMessage
                id={error}
                defaultMessage={error}
              />
            </ErrorMessage>
          )}

          {isEmpty && (
            <ErrorMessage>
              <FormattedMessage id={emptyMessage} />
            </ErrorMessage>
          )}

          {!error && hasData && (
            <TableList
              key={tableRenderKey}
              columns={columns}
              country={currentUser.taxResidence}
              data={data}
              options={options}
              user={currentUser}
              onOrderChange={handleOrderChange}
            />
          )}

          {loading && !error && (
            <LoadingSpinner
              message={loadingMessage}
              fade
            />
          )}
        </>
      ) : (
        <ErrorMessage>
          <FormattedMessage id="app.columns.noneSelected" />
        </ErrorMessage>
      )}

      <DialogColumnSelector
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        availableColumns={availableColumns}
        selectedColumns={selectedColumns}
        onChange={setSelectedColumns}
      />
    </>
  );
};

export default ReportTable;
